import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, forwardRef } from 'react';
import './AutocompleteInput.css';

const MAX_SUGGESTIONS = 8;

/**
 * Zero-dependency autocomplete text input.
 * - options: [{ id, name, nameLower, categoryName, gender, defaultDuration, subServices }]
 * - onSelect(option) fires when a suggestion is picked.
 * - onCreateNew(text) fires from the trailing "+ Add ... to master catalog" item.
 * Free text is always allowed; the dropdown is assistance, not validation.
 */
const AutocompleteInput = forwardRef(function AutocompleteInput(
  { value, onChange, onSelect, onCreateNew, options, placeholder, disabled, onKeyDown, onPaste, className, createLabelText = 'to master catalog' },
  ref
) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [dropdownPos, setDropdownPos] = useState(null);
  const debounceRef = useRef(null);
  const skipNextFilter = useRef(false);
  const inputRef = useRef(null);

  // Track the input node locally while still honoring the forwarded ref
  const setRefs = useCallback(
    (el) => {
      inputRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) ref.current = el;
    },
    [ref]
  );

  const query = (value || '').trim().toLowerCase();

  useEffect(() => {
    if (skipNextFilter.current) {
      skipNextFilter.current = false;
      return undefined;
    }
    if (!open || !query) {
      setSuggestions([]);
      return undefined;
    }
    debounceRef.current = setTimeout(() => {
      const starts = [];
      const includes = [];
      for (const opt of options || []) {
        const name = opt.nameLower || (opt.name || '').toLowerCase();
        if (name.startsWith(query)) starts.push(opt);
        else if (name.includes(query)) includes.push(opt);
        if (starts.length >= MAX_SUGGESTIONS) break;
      }
      setSuggestions([...starts, ...includes].slice(0, MAX_SUGGESTIONS));
      setHighlightIndex(0);
    }, 150);
    return () => clearTimeout(debounceRef.current);
  }, [query, open, options]);

  const hasExactMatch = suggestions.some(
    (s) => (s.nameLower || (s.name || '').toLowerCase()) === query
  );
  const showCreateItem = !!onCreateNew && !!query && !hasExactMatch;
  const itemCount = suggestions.length + (showCreateItem ? 1 : 0);
  const dropdownVisible = open && itemCount > 0;

  // The dropdown is position:fixed so scroll containers (like the grid's
  // table wrapper) can't clip it; when there's no room below the input it
  // opens upward instead.
  const updateDropdownPosition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < 300 && rect.top > spaceBelow;
    const maxHeight = Math.min(320, (openUp ? rect.top : spaceBelow) - 12);
    setDropdownPos({
      left: rect.left,
      width: Math.max(rect.width, 260),
      maxHeight: Math.max(maxHeight, 120),
      top: openUp ? undefined : rect.bottom + 2,
      bottom: openUp ? window.innerHeight - rect.top + 2 : undefined
    });
  }, []);

  useLayoutEffect(() => {
    if (dropdownVisible) updateDropdownPosition();
  }, [dropdownVisible, itemCount, updateDropdownPosition]);

  useEffect(() => {
    if (!dropdownVisible) return undefined;
    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [dropdownVisible, updateDropdownPosition]);

  const closeDropdown = () => {
    setOpen(false);
    setSuggestions([]);
    setHighlightIndex(0);
  };

  const pick = (index) => {
    if (index < suggestions.length) {
      skipNextFilter.current = true;
      onSelect(suggestions[index]);
    } else if (showCreateItem) {
      onCreateNew((value || '').trim());
    }
    closeDropdown();
  };

  const handleKeyDown = (e) => {
    if (dropdownVisible) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setHighlightIndex((i) => (i + 1) % itemCount);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setHighlightIndex((i) => (i - 1 + itemCount) % itemCount);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        pick(highlightIndex);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        closeDropdown();
        return;
      }
      if (e.key === 'Tab') {
        closeDropdown();
      }
    }
    if (onKeyDown) onKeyDown(e);
  };

  return (
    <div className="autocomplete-wrapper">
      <input
        ref={setRefs}
        type="text"
        className={className || ''}
        value={value || ''}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        onChange={(e) => {
          setOpen(true);
          onChange(e.target.value);
        }}
        onFocus={() => setOpen(true)}
        onBlur={closeDropdown}
        onKeyDown={handleKeyDown}
        onPaste={onPaste}
      />
      {dropdownVisible && dropdownPos && (
        <ul
          className="autocomplete-dropdown"
          style={{
            left: dropdownPos.left,
            width: dropdownPos.width,
            maxHeight: dropdownPos.maxHeight,
            top: dropdownPos.top,
            bottom: dropdownPos.bottom
          }}
        >
          {suggestions.map((opt, index) => (
            <li
              key={opt.id || opt.name}
              className={`autocomplete-item ${index === highlightIndex ? 'highlighted' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(index);
              }}
              onMouseEnter={() => setHighlightIndex(index)}
            >
              <span className="autocomplete-item-name">{opt.name}</span>
              <span className="autocomplete-item-meta">
                {[opt.categoryName, opt.gender, opt.defaultDuration ? `${opt.defaultDuration} mins` : '']
                  .filter(Boolean)
                  .join(' · ')}
                {opt.subServices?.length ? ` · ${opt.subServices.length} sub services` : ''}
              </span>
            </li>
          ))}
          {showCreateItem && (
            <li
              className={`autocomplete-item autocomplete-create ${highlightIndex === suggestions.length ? 'highlighted' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(suggestions.length);
              }}
              onMouseEnter={() => setHighlightIndex(suggestions.length)}
            >
              + Add "{(value || '').trim()}" {createLabelText}
            </li>
          )}
        </ul>
      )}
    </div>
  );
});

export default AutocompleteInput;
